import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { Button, Alert, Card, Spin } from "antd";
import { ExclamationCircleOutlined, HomeOutlined } from "@ant-design/icons";
import useRequest from "@/utils/useRequest";
import { useCommonStore } from "@/store/useCommonStore";

const CallbackPage = () => {
    const { request } = useRequest();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const setUser = useCommonStore(state => state.login);
    
    // 添加状态管理
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const callbackRequest = async (code, state) => {
        try {
            setLoading(true);
            const result = await request("/api/v2/public/auth/callback", "POST", "json", { code, state });
            console.log("callback 结果", result);
            
            // 判断返回结果是否成功
            if (result.success) {
                // 设置用户信息到store
                if (result.data) {
                    // 直接设置用户信息，类似login方法的逻辑
                    useCommonStore.setState(state => {
                        state.user = {
                            ...result.data,
                        };
                    });
                }
                
                // 重定向到home页面
                navigate("/");
            } else {
                console.error("登录失败：", result.message);
                // 设置错误状态，显示错误信息
                setError(true);
                setErrorMessage(result.message || "登录失败，请重试");
                setLoading(false);
            }
        } catch (err) {
            console.error("回调请求失败：", err);
            // 处理请求异常，显示错误信息
            setError(true);
            setErrorMessage("网络请求失败，请检查网络连接后重试");
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate("/login");
    };

    useEffect(() => {
        const storedState = sessionStorage.getItem("auth_state"); // 获取缓存的 state
        const receivedState = searchParams.get("state"); // 从 URL 参数中获取 state
        const code = searchParams.get("code"); // 获取授权码

        // 验证 state
        if (!storedState || storedState !== receivedState) {
            console.error("State 验证失败！");
            // 处理验证失败逻辑，显示错误信息
            setError(true);
            setErrorMessage("安全验证失败，请重新登录");
            setLoading(false);
            return;
        }

        console.log("State 验证通过，授权码为:", code);
        // 使用授权码请求访问令牌
        callbackRequest(code, receivedState);
    }, [searchParams]);

    // 加载中界面
    if (loading && !error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-96 text-center shadow-lg">
                    <Spin size="large" />
                    <div className="mt-4 text-lg text-gray-600">正在处理登录回调...</div>
                    <div className="mt-2 text-sm text-gray-400">请稍候，系统正在验证您的身份</div>
                </Card>
            </div>
        );
    }

    // 错误界面
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-96 shadow-lg">
                    <div className="text-center">
                        <ExclamationCircleOutlined className="text-6xl text-red-500 mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">登录失败</h2>
                        
                        <Alert
                            message="错误信息"
                            description={errorMessage}
                            type="error"
                            showIcon
                            className="mb-6 text-left"
                        />
                        
                        <div className="space-y-3">
                            <Button 
                                type="primary" 
                                size="large" 
                                icon={<HomeOutlined />}
                                onClick={handleBackToLogin}
                                className="w-full"
                            >
                                返回登录页面
                            </Button>
                            
                            <Button 
                                size="large" 
                                onClick={() => window.location.reload()}
                                className="w-full"
                            >
                                重新尝试
                            </Button>
                        </div>
                        
                        <div className="mt-6 text-xs text-gray-400">
                            如果问题持续存在，请联系系统管理员
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return null;
};

export default CallbackPage;
